import {
  type TDiarizationProvider,
  type TEnsembleMergeMode,
  TRANSCRIPTION_PROVIDER_IDS,
  type TTranscriptionMode,
  type TTranscriptionProvider,
} from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Modal } from "@mantine/core"
import { useCallback, useEffect, useState } from "react"
import { useCompanyApi } from "../../hooks/useCompanyApi"
import { useCompanyQuery } from "../../hooks/useCompanyQuery"
import {
  fetchAdapters,
  fetchDefaultPrompt,
  fetchReprocessEstimate,
  fetchSettings,
  reprocessMeeting,
} from "../../lib/api"
import { notifyError } from "../../lib/notify"
import { queryKeys } from "../../lib/query-keys"
import type { IAdapterInfo } from "../../lib/types/api"
import {
  type IEnsembleEstimateBreakdown,
  resolveWorkspaceDiarizationProvider,
  resolveWorkspaceMergeMode,
  resolveWorkspaceTranscriptionMode,
} from "./helpers"
import type { IProps } from "./interfaces"
import { ReprocessFormView } from "./ReprocessFormView"
import type { IReprocessFormSubmit, TReprocessMode } from "./ReprocessFormView/interfaces"
import { ReprocessProgress } from "./ReprocessProgress"
import { useReprocessProgress } from "./useReprocessProgress"

/**
 * Maps the dumb view's `mode` selection onto the backend's `fromAlias`
 * graph-step identifier (a single rerun root). "full" omits it for a
 * full pipeline rerun. The "images" mode is special-cased to `fromAliases`
 * (multi-root) in `handleSubmit` because it fans out to all three image
 * steps. Source of truth for the alias names + descendant sets:
 * `backend/src/pipeline/templates/{meeting-intelligence,dnd-companion}.ts`.
 *
 * Every value here is pinned against both backend template graphs by
 * `backend/src/__tests__/pure-unit/reprocess-from-alias.test.ts`.
 */
const FROM_ALIAS: Record<Exclude<TReprocessMode, "images">, string | undefined> = {
  full: undefined,
  speakers: "speaker-resolution",
  analysis: "final-analysis",
  chapters: "cluster-chapters",
}

/**
 * The "images" mode reruns all three image steps as independent roots — the
 * monolithic `image-generation` alias was split into these. Only the
 * dnd-companion template declares them; the backend intersects this set with
 * the meeting's resolved graph and 400s if none are present.
 */
const IMAGE_ALIASES: readonly string[] = ["image-scenes", "image-plaques", "image-portraits"]

function isTranscriptionProvider(value: string): value is TTranscriptionProvider {
  return TRANSCRIPTION_PROVIDER_IDS.some((id) => id === value)
}

function toTranscriptionBackend(name: string | null): TTranscriptionProvider | undefined {
  return name !== null && isTranscriptionProvider(name) ? name : undefined
}

type TPhase = "form" | "running" | "done" | "error"

/**
 * Smart wrapper for the reprocess form. Owns the three API calls
 * (`fetchAdapters`, `fetchDefaultPrompt`, `reprocessMeeting`), the
 * in-flight loading state, and error notifications. ReprocessFormView
 * holds the local form/draft state and renders the modal.
 *
 * After a successful submit the wrapper switches the modal contents from
 * the form to a live progress view (`ReprocessProgress`) that subscribes
 * to the meeting's processing-stage SSE events. The user can keep the
 * modal open to watch the pipeline tick through, or close it and let the
 * pipeline finish in the background — the parent's `onReprocessed`
 * callback fires in either case so the meeting view refetches.
 */
export function ReprocessForm({
  meetingId,
  meetingLanguage,
  hasRecording,
  templateId,
  hasManualEdits,
  onReprocessed,
  opened,
  onClose,
}: IProps): React.JSX.Element {
  const company = useCompanyApi()
  const { i18n } = useLingui()
  const [submitting, setSubmitting] = useState(false)
  const [phase, setPhase] = useState<TPhase>("form")
  const progress = useReprocessProgress({
    enabled: phase === "running",
    meetingId,
  })

  // When the SSE stream signals completion (meeting phase=summarized) or an
  // error stage, transition the modal — the user sees a final state and a
  // primary "Close" button.
  useEffect(() => {
    if (phase !== "running") return
    if (progress.done) setPhase("done")
    else if (progress.errored) setPhase("error")
  }, [phase, progress.done, progress.errored])

  // Reset to the form when the modal closes externally so the next open
  // shows the picker again instead of stale progress state.
  useEffect(() => {
    if (!opened) setPhase("form")
  }, [opened])

  // Workspace speaker-turns default (org key → platform default → "deepgram"),
  // resolved client-side from the same settings map the settings page uses.
  const settingsQuery = useCompanyQuery({
    key: queryKeys.settings,
    queryFn: (c) => fetchSettings(c),
    enabled: Boolean(opened),
    staleTime: 60_000,
  })
  const workspaceDiarizationProvider = resolveWorkspaceDiarizationProvider(settingsQuery.data)
  const workspaceTranscriptionMode = resolveWorkspaceTranscriptionMode(settingsQuery.data)
  const workspaceMergeMode = resolveWorkspaceMergeMode(settingsQuery.data)

  const handleEstimate = useCallback(
    async (choice: {
      mode: TReprocessMode
      adapter: string | null
      diarizationProvider?: TDiarizationProvider
      transcriptionMode?: TTranscriptionMode
      mergeMode?: TEnsembleMergeMode
      remerge?: boolean
    }): Promise<{ credits: number; ensemble?: IEnsembleEstimateBreakdown } | null> => {
      if (!company) return null
      try {
        const e = await fetchReprocessEstimate(company, meetingId, {
          mode: choice.mode,
          transcriptionBackend: toTranscriptionBackend(choice.adapter),
          diarizationProvider: choice.diarizationProvider,
          transcriptionMode: choice.transcriptionMode,
          mergeMode: choice.mergeMode,
          remerge: choice.remerge,
        })
        const ensemble = e.breakdown.ensemble
        return ensemble ? { credits: e.credits, ensemble } : { credits: e.credits }
      } catch {
        // An estimate is a convenience; a failure must never block the dialog.
        return null
      }
    },
    [company, meetingId],
  )

  const handleLoadAdapters = useCallback(async (): Promise<IAdapterInfo[]> => {
    if (!company) return []
    const r = (await fetchAdapters(company)) as { adapters: IAdapterInfo[] }
    return r.adapters
  }, [company])

  const handleLoadDefaultPrompt = useCallback(async (): Promise<string> => {
    if (!company) return ""
    const r = await fetchDefaultPrompt(company)
    return r.prompt
  }, [company])

  const handleSubmit = useCallback(
    async (payload: IReprocessFormSubmit): Promise<void> => {
      if (!company) return
      setSubmitting(true)
      try {
        // The images mode fans out to three roots (fromAliases); every other
        // mode maps to a single fromAlias (or none, for "full").
        const modeParams =
          payload.mode === "images"
            ? { fromAliases: [...IMAGE_ALIASES], forceImages: payload.forceImages }
            : { fromAlias: FROM_ALIAS[payload.mode] }
        await reprocessMeeting(company, meetingId, {
          systemPrompt: payload.systemPrompt ?? undefined,
          transcriptionBackend: toTranscriptionBackend(payload.adapter),
          ...(payload.diarizationProvider ? { diarizationProvider: payload.diarizationProvider } : {}),
          ...(payload.transcriptionMode ? { transcriptionMode: payload.transcriptionMode } : {}),
          ...(payload.mergeMode ? { mergeMode: payload.mergeMode } : {}),
          ...(payload.remerge ? { remerge: true } : {}),
          ...modeParams,
        })
        // Don't close the modal — flip into the progress view. The parent
        // refetches once when we transition into running, so the optimistic
        // "is_intermediate=true" state lands in the route immediately.
        onReprocessed()
        setPhase("running")
      } catch (err) {
        notifyError(err, i18n._(msg`Reprocess failed`))
        // Rethrow so the view keeps the modal open with the user's draft intact.
        throw err
      } finally {
        setSubmitting(false)
      }
    },
    [company, meetingId, onReprocessed, i18n],
  )

  const handleProgressClose = useCallback((): void => {
    // Always refetch on close from the progress view — the meeting may have
    // finished after the user dismissed the modal but before the SSE done
    // signal arrived (race on the parent's stale meeting object).
    onReprocessed()
    onClose?.()
  }, [onReprocessed, onClose])

  if (phase !== "form") {
    return (
      <Modal
        opened={opened ?? false}
        onClose={handleProgressClose}
        title={
          phase === "done"
            ? i18n._(msg`Reprocess complete`)
            : phase === "error"
              ? i18n._(msg`Reprocess error`)
              : i18n._(msg`Reprocessing meeting`)
        }
        centered
        size="lg"
        // The pipeline keeps running in the background — clicking the
        // overlay shouldn't feel like an "abort". Allow it but the close
        // handler still fires `onReprocessed` so the meeting view refreshes.
        closeOnClickOutside
      >
        <ReprocessProgress progress={progress} onClose={handleProgressClose} />
      </Modal>
    )
  }

  return (
    <ReprocessFormView
      templateId={templateId}
      hasRecording={Boolean(hasRecording)}
      hasManualEdits={hasManualEdits}
      meetingLanguage={meetingLanguage}
      workspaceDiarizationProvider={workspaceDiarizationProvider}
      workspaceTranscriptionMode={workspaceTranscriptionMode}
      workspaceMergeMode={workspaceMergeMode}
      onEstimate={handleEstimate}
      loading={submitting}
      opened={opened}
      onLoadAdapters={handleLoadAdapters}
      onLoadDefaultPrompt={handleLoadDefaultPrompt}
      onSubmit={handleSubmit}
      onClose={onClose}
    />
  )
}
